/**
 * Lead magnets — a free downloadable guide handed to a visitor the moment
 * they verify their phone at signup, scoped to the funnel they came in on.
 *
 * The /sat and /act funnels ("Take placement test" → phone signup) each ship
 * a magnet. Adding another test's playbook is a single entry in
 * ./lead-magnets-data plus dropping its PDF in apps/portal/public/ (then
 * `pnpm magnets:upload`) — no wiring changes. The map is keyed by the
 * test-landing slug (see routes/testLanding/configs.ts), which is the same
 * slug stashed by useStartDiagnostic and derived from the landing pathname, so
 * eligibility is decided by "which test page did they sign up on".
 *
 * This module is the runtime/UI layer: it re-exports the data layer
 * (./lead-magnets-data) and adds the two browser-only helpers below, so every
 * importer keeps a single "../lib/lead-magnets.js" entrypoint while the Node
 * upload script imports the DOM-free data module directly.
 *
 * Serving: in production the PDF is fetched from the shared CloudFront CDN
 * (`${VITE_VIDEO_CDN}/lead-magnets/<file>`, uploaded by
 * scripts/upload-lead-magnets.ts) — see leadMagnetHref(). When the CDN var is
 * unset (local dev) it falls back to the same-origin public path, so `vite dev`
 * serves it straight from apps/portal/public/ with no CDN.
 */
import { getVideoCdn } from "./video-cdn.js";
import { LEAD_MAGNET_CDN_PREFIX, type LeadMagnet } from "./lead-magnets-data.js";

export type { LeadMagnet } from "./lead-magnets-data.js";
export {
  LEAD_MAGNETS_LIST,
  LEAD_MAGNET_CDN_PREFIX,
  leadMagnetForSlug,
} from "./lead-magnets-data.js";

/**
 * The URL to fetch a magnet from: the CloudFront CDN
 * (`${VITE_VIDEO_CDN}/lead-magnets/<file>`) in production, else the same-origin
 * public path (`magnet.file`) so local dev serves it from apps/portal/public/
 * with no CDN. The uploaded S3 object carries
 * `Content-Disposition: attachment; filename="<downloadName>"`, so the
 * cross-origin download still saves with the right name even though browsers
 * ignore an `<a download>` attribute across origins.
 */
export function leadMagnetHref(magnet: LeadMagnet): string {
  const cdn = getVideoCdn();
  if (!cdn) return magnet.file;
  return `${cdn}/${LEAD_MAGNET_CDN_PREFIX}/${magnet.file.replace(/^\//, "")}`;
}

/**
 * Trigger a browser download of a file via a transient anchor.
 * Best-effort: a programmatic download fired outside a direct click handler
 * can be blocked by the browser, which is why every caller also surfaces a
 * manual download affordance (the toast's action button). For a cross-origin
 * (CDN) href the browser ignores the `download` attribute, so the saved
 * filename comes from the object's Content-Disposition header instead.
 */
export function triggerDownload(href: string, filename: string): void {
  try {
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    // Swallow — the manual download affordance is the guaranteed path.
  }
}
